import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { Subject, takeUntil, of, combineLatest, Observable, EMPTY } from 'rxjs';
import { switchMap, filter, take, debounceTime, map } from 'rxjs/operators';

import { VIRoutingMap } from '@web/app/routing/routes';
import { CameraStatus } from '@live/components/live-camera-item/interfaces';
import { Camera } from '@live/reducers/live-cameras.reducer';
import { VIEdgeLiveRoutingMap } from '@live/routes/live-edge-routes.config';

import { TranslateHelperService } from '@common/modules/translation/services/translate-helper.service';
import { FocusManagerService } from '@common/modules/accessibility/focus-manager.service';
import { FeatureSwitch, SideMenuOption } from '@common/modules/core/services/interfaces';
import { EventCategory, TrackService } from '@common/modules/core/services/track';
import { SharedModule } from '@common/modules/shared/shared.module';
import { FeatureSwitchService } from '@common/modules/core/services/feature-switch/feature-switch.service';

import { EdgeExtensionsStoreService } from '../../../core/services/edge-extensions-store.service';
import { LiveStoreService } from '../../../edge/live/services/live-edge-store.service';
import { resources } from './resources';
import { CoreStoreService } from '../../../core/services/core-store.service';
import { IExtensionMenuItem } from './interfaces';

import IContractCamera = Microsoft.VideoIndexer.Live.Contracts.ICamera;
import IConnectedClusterExtension = Microsoft.VideoIndexer.Contracts.IConnectedClusterExtension;

@Component({
  selector: 'vi-edge-extensions-sub-menu',
  templateUrl: './edge-extensions-sub-menu.component.html',
  styleUrls: ['./edge-extensions-sub-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SharedModule]
})
export class EdgeExtensionsSubMenuComponent implements OnInit, OnDestroy {
  public menuItems: IExtensionMenuItem[];
  public resources = resources;
  public CameraStatus = CameraStatus;
  public hasSelectedEdgeExtension: boolean;
  public isCamerasLoading: boolean = false;
  public isPinnedCamerasLoading: boolean = false;
  public pinnedCameras: IContractCamera[] = [];
  public cameras: IContractCamera[] = [];
  public unpinnedCamerasCount?: number;
  public isCamerasFullyLoaded: boolean = false;
  public selectedCamera: Camera | null = null;

  private selectedEdgeExtensionId: string = '';

  private readonly CLOUD_ITEM_CLASS = 'cloud';
  private readonly MULTI_EXTENSIONS_CLASS = 'multi-extensions';
  private readonly EXTENSION_CLASS_PREFIX = 'extension';
  private readonly destroy$ = new Subject<void>();

  private readonly allCamerasCount$: Observable<number> = this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(
    switchMap(isLoaded => {
      if (isLoaded && this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
        return this.liveStoreService.selectCamerasNextPage$.pipe(
          takeUntil(this.destroy$),
          map(cameras => cameras.totalCount ?? 0)
        );
      }
      return of(0);
    })
  );

  private readonly pinnedCamerasCount$: Observable<number> = this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(
    switchMap(isLoaded => {
      if (isLoaded && this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
        return this.liveStoreService.selectPinnedCamerasNextPage$.pipe(
          takeUntil(this.destroy$),
          map(pinnedCameras => pinnedCameras.totalCount ?? 0)
        );
      }
      return of(0);
    })
  );

  private readonly cameras$: Observable<IContractCamera[]> = this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(
    switchMap(isLoaded => {
      if (isLoaded && this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
        return this.liveStoreService.selectCameras$.pipe(filter(cameras => Array.isArray(cameras)));
      }
      return of([]);
    })
  );

  private readonly pinnedCameras$: Observable<IContractCamera[]> = this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(
    switchMap(isLoaded => {
      if (isLoaded && this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
        return this.liveStoreService.selectPinnedCameras$.pipe(filter(cameras => Array.isArray(cameras)));
      }
      return of([]);
    })
  );

  private isMediaSideMenuOptionSelected: boolean;
  private extensionsCount: number;

  constructor(
    public edgeExtensionsStore: EdgeExtensionsStoreService,
    public coreStore: CoreStoreService,
    private translate: TranslateHelperService,
    private cdr: ChangeDetectorRef,
    private focusManager: FocusManagerService,
    private trackService: TrackService,
    private liveStoreService: LiveStoreService,
    private router: Router,
    private featureSwitchService: FeatureSwitchService
  ) {}

  public ngOnInit(): void {
    this.translate.translateResourcesInstant(resources);
    this.setupDataStreams();
    this.setupSubscriptions();
    this.setupNavigationHandler();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isSingleExtension(): boolean {
    return this.extensionsCount === 1;
  }

  public handleSelectItem(item: IExtensionMenuItem): void {
    if (!item.value) {
      // Always navigate to media gallery for "My Cloud" item
      if (this.hasSelectedEdgeExtension) {
        this.edgeExtensionsStore.clearSelectedEdgeExtension();
      }
      this.coreStore.navigate([`${VIRoutingMap.mediaGallery.path}`], null, null, true);
    } else if (item.value === this.MULTI_EXTENSIONS_CLASS) {
      this.coreStore.navigate([`${VIRoutingMap.edgeExtensions.path}`], null, null, true);
    } else {
      this.selectEdgeExtension(item.value);
    }
  }

  public toggleExtension(item: IExtensionMenuItem): void {
    item.expanded = !item.expanded;

    // Fetch cameras when extension is expanded
    if (item.expanded) {
      this.trackService.track('extension_submenu.expanded', {
        category: EventCategory.EDGE
      });

      this.refreshCameraData();
    }

    this.handleSelectItem(item);
    this.cdr.markForCheck();
  }

  public refreshCameraData(): void {
    // Check if live module is loaded before accessing selectors
    this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(take(1)).subscribe(isLoaded => {
      if (!isLoaded || !this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
        return;
      }

      // Check if we have existing data first
      this.liveStoreService.selectPinnedCameras$.pipe(take(1)).subscribe(existingCameras => {
        const hasExistingData = existingCameras?.length > 0;
        const needsInitialLoad = !hasExistingData && !this.isCamerasFullyLoaded;

        // First check if we need to load all cameras for total count
        combineLatest([
          of(existingCameras),
          of(hasExistingData),
          of(needsInitialLoad),
          this.liveStoreService.selectCamerasNextPage$.pipe(takeUntil(this.destroy$))
        ]).subscribe(([cameras, hasData, needsLoad, nextPage]) => {
          const needsTotalCount = !nextPage?.totalCount && nextPage?.totalCount !== 0;

          // Then handle pinned cameras loading
          if (
            !this.isCamerasLoading &&
            !this.isPinnedCamerasLoading &&
            this.hasSelectedEdgeExtension &&
            (needsLoad || (hasData && cameras.length < 100))
          ) {
            // Load pinned cameras, ensuring we get at least 100 for the side menu
            this.liveStoreService.listPinnedCameras({ pageSize: 100 });
          }

          // Independently load all cameras if we need the total count
          if (needsTotalCount) {
            this.liveStoreService.listCameras();
          }
        });
      });
    });
  }

  public async handleCameraSelect(camera: IContractCamera): Promise<void> {
    if (camera.status !== CameraStatus.Online) {
      return;
    }

    this.trackService.track('extension_submenu.camera.clicked', {
      category: EventCategory.EDGE,
      data: {
        cameraId: camera.id
      }
    });

    this.refreshCameraData();
    this.liveStoreService.goToCameraPage(camera.id);
  }

  public async showHiddenCameras(item: IExtensionMenuItem): Promise<void> {
    this.trackService.track('extension_submenu.view_all_cameras.clicked', {
      category: EventCategory.EDGE,
      data: {
        hiddenCamerasCount: item.hiddenCamerasCount
      }
    });

    this.refreshCameraData();
    this.liveStoreService.goToCameraManagementAction();
  }

  private initializeCameraData(
    accountExtensions: IConnectedClusterExtension[],
    selectedEdgeExtensionId: string
  ): Observable<{ cameras: IContractCamera[]; pinnedCameras: IContractCamera[] }> {
    const selectedExtension = accountExtensions.find(ext => ext.id === selectedEdgeExtensionId);
    const liveConfiguration =
      selectedExtension?.properties?.configurationSettings?.['videoIndexer.liveStreamEnabled'] ||
      selectedExtension?.properties?.configurationSettings?.['videoIndexer.liveVideoStreamEnabled'];
    const isLiveEnabled = liveConfiguration?.toLowerCase() === 'true';

    if (isLiveEnabled && selectedEdgeExtensionId) {
      this.trackService.track('extension_submenu.loading_cameras', {
        category: EventCategory.EDGE
      });

      // Data loading is now handled by refreshCameraData() which checks loading states
      // Only call it if we haven't loaded the data yet
      if (!this.isCamerasFullyLoaded) {
        this.refreshCameraData();
      }

      return combineLatest({ cameras: this.cameras$, pinnedCameras: this.pinnedCameras$ });
    }

    return of({ cameras: [], pinnedCameras: [] });
  }

  private setupDataStreams(): void {
    this.edgeExtensionsStore.accountEdgeExtensions$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(accountExtensions => {
          this.extensionsCount = accountExtensions.length;

          return combineLatest([of(accountExtensions), this.coreStore.selectSideMenuSelectedOption()]).pipe(
            switchMap(([extensions, sideMenuOption]) => {
              this.isMediaSideMenuOptionSelected = sideMenuOption === SideMenuOption.Media;

              return this.edgeExtensionsStore.selectedEdgeExtensionId$.pipe(
                switchMap(selectedEdgeExtensionId =>
                  combineLatest([
                    of(extensions),
                    of(selectedEdgeExtensionId),
                    of(sideMenuOption),
                    this.initializeCameraData(extensions, selectedEdgeExtensionId)
                  ])
                )
              );
            })
          );
        })
      )
      .subscribe(([accountExtensions, selectedEdgeExtensionId]) => {
        this.initMenuItems(accountExtensions, selectedEdgeExtensionId);
        this.cdr.markForCheck();
        this.focusEdgeExtensionItem(selectedEdgeExtensionId);
      });
  }

  public get shouldSelectCamera(): boolean {
    const urlPath = this.router.url.split('?')[0];
    const liveStreamRoute = VIEdgeLiveRoutingMap.cameraLiveStreamTab.path.split('/');
    const recordingRoute = VIEdgeLiveRoutingMap.cameraRecordingsTab.path.split('/');
    return urlPath.endsWith(liveStreamRoute[liveStreamRoute.length - 1]) || urlPath.endsWith(recordingRoute[recordingRoute.length - 1]);
  }

  private setupSubscriptions(): void {
    this.edgeExtensionsStore.hasSelectedEdgeExtension$.pipe(takeUntil(this.destroy$)).subscribe(hasSelectedEdgeExtension => {
      this.hasSelectedEdgeExtension = hasSelectedEdgeExtension;
    });

    if (this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge) === false) {
      // If the LiveEdge feature is not enabled, set cameras to an empty array and mark them as fully loaded
      this.isCamerasFullyLoaded = true;
      return;
    }

    // Subscribe to camera and pinned camera changes, along with their loading states
    this.edgeExtensionsStore.isLiveModuleLoaded$
      .pipe(
        filter(isLoaded => isLoaded),
        take(1),
        switchMap(() =>
          combineLatest({
            pinnedCameras: this.pinnedCameras$,
            allCamerasCount: this.allCamerasCount$,
            pinnedCamerasCount: this.pinnedCamerasCount$,
            accountExtensions: this.edgeExtensionsStore.accountEdgeExtensions$,
            selectedEdgeExtensionId: this.edgeExtensionsStore.selectedEdgeExtensionId$,
            handshakeFailed: this.edgeExtensionsStore.isSelectedEdgeExtensionError$,
            isLiveStreamEnabled: this.edgeExtensionsStore.isLiveStreamEnabled$,
            isPinnedCamerasLoaded: this.liveStoreService.selectIsPinnedCamerasListLoaded$,
            isCamerasListLoaded: this.liveStoreService.selectIsCamerasListLoaded$,
            isPinnedCamerasLoading: this.liveStoreService.selectIsPinnedCamerasListLoading$,
            selectedCamera: this.liveStoreService.selectedCamera$
          })
        )
      )
      // We're debouncing here to avoid multiple emissions in quick succession with stale data, which can
      // affect the Loading state.
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(
        ({
          pinnedCameras,
          allCamerasCount,
          pinnedCamerasCount,
          accountExtensions,
          selectedEdgeExtensionId,
          isCamerasListLoaded,
          isPinnedCamerasLoading,
          handshakeFailed,
          isLiveStreamEnabled,
          isPinnedCamerasLoaded,
          selectedCamera
        }) => {
          if (this.selectedEdgeExtensionId !== selectedEdgeExtensionId && this.isCamerasFullyLoaded) {
            this.isCamerasFullyLoaded = false;
          }

          if (isCamerasListLoaded) {
            this.isCamerasFullyLoaded = true;
          }

          const unpinnedCount = allCamerasCount - pinnedCamerasCount;
          this.unpinnedCamerasCount = unpinnedCount >= 0 ? unpinnedCount : 0;

          if (handshakeFailed || (!isLiveStreamEnabled && selectedEdgeExtensionId !== '')) {
            this.pinnedCameras = [];
            this.isCamerasFullyLoaded = true;
          }

          // Update pinned cameras only if the cameras are fully loaded, this prevents flickering when re-fetching the pinned cameras
          // since the initial fetch updates the pinnedCameras array to be [] before data arrives.
          if (this.isCamerasFullyLoaded && isPinnedCamerasLoaded) {
            this.pinnedCameras = pinnedCameras;
          }

          this.selectedCamera = selectedCamera;
          this.isPinnedCamerasLoading = isPinnedCamerasLoading;
          this.selectedEdgeExtensionId = selectedEdgeExtensionId;
          this.initMenuItems(accountExtensions, selectedEdgeExtensionId);
          this.cdr.markForCheck();
        }
      );
  }

  private setupNavigationHandler(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        switchMap((event: NavigationEnd) => {
          if (!this.menuItems) {
            return EMPTY;
          }

          return this.edgeExtensionsStore.accountEdgeExtensions$.pipe(
            take(1),
            switchMap(accountExtensions =>
              this.edgeExtensionsStore.selectedEdgeExtensionId$.pipe(
                take(1),
                switchMap(selectedEdgeExtensionId =>
                  combineLatest([
                    of(accountExtensions),
                    of(selectedEdgeExtensionId),
                    this.initializeCameraData(accountExtensions, selectedEdgeExtensionId)
                  ])
                )
              )
            )
          );
        })
      )
      .subscribe(([accountExtensions, selectedEdgeExtensionId]) => {
        this.initMenuItems(accountExtensions, selectedEdgeExtensionId);
        this.cdr.markForCheck();
        this.focusEdgeExtensionItem(selectedEdgeExtensionId);
      });
  }

  private initMenuItems(accountExtensions: IConnectedClusterExtension[], selectedEdgeExtensionId: string): void {
    const currentUrl = this.router.url;

    const cloudItem: IExtensionMenuItem = {
      value: '',
      selected: this.isMediaSideMenuOptionSelected && currentUrl.includes(VIRoutingMap.mediaGallery.path) && !this.hasSelectedEdgeExtension,
      text: resources.MyCloud,
      class: this.CLOUD_ITEM_CLASS,
      isExtension: false
    };

    this.menuItems = [cloudItem];
    const multiExtensionsItem: IExtensionMenuItem = {
      value: this.MULTI_EXTENSIONS_CLASS,
      selected: this.isMediaSideMenuOptionSelected && this.router.url.includes(VIRoutingMap.edgeExtensions.path),
      text: resources.EdgeExtensionsPageTitle,
      class: this.MULTI_EXTENSIONS_CLASS,
      isExtension: false
    };

    // Always show "Arc extensions" menu item when on edge-extensions route
    if (currentUrl.includes(VIRoutingMap.edgeExtensions.path)) {
      this.menuItems.push(multiExtensionsItem);
    } else if (this.isSingleExtension || selectedEdgeExtensionId) {
      let extensionsToAddToMenu = accountExtensions;

      if (!this.isSingleExtension && selectedEdgeExtensionId) {
        extensionsToAddToMenu = accountExtensions.filter(extension => extension.id === selectedEdgeExtensionId);
      }

      for (const extension of extensionsToAddToMenu) {
        if (!extension?.identity?.principalId) {
          this.trackService.track('extension_submenu.add_extension.skipped', {
            category: EventCategory.EDGE,
            data: {
              extensionName: extension?.name,
              extensionType: extension?.type,
              hasIdentity: !!extension?.identity
            }
          });
          continue;
        }

        const visibleCameras = this.pinnedCameras.map(camera => ({
          id: camera.id,
          name: camera.name,
          isPinned: true,
          clusterExtensionId: extension.id
        }));

        visibleCameras.sort((a, b) => a.name.localeCompare(b.name));

        this.menuItems.push({
          value: extension.id,
          selected: this.isMediaSideMenuOptionSelected && selectedEdgeExtensionId === extension.id,
          text: extension.name,
          class: `${this.EXTENSION_CLASS_PREFIX}_${extension.identity.principalId}`,
          isExtension: true,
          expanded: selectedEdgeExtensionId === extension.id,
          cameras: visibleCameras,
          hiddenCamerasCount: this.unpinnedCamerasCount
        });
      }
    } else {
      this.menuItems.push(multiExtensionsItem);
    }
  }

  private focusEdgeExtensionItem(selectedEdgeExtensionId: string): void {
    const menuItem = this.menuItems.find(item => item.value === selectedEdgeExtensionId);
    if (menuItem) {
      this.focusManager.focusByQuery(`.${menuItem.class}`, this.cdr);
    }
  }

  private selectEdgeExtension(id: string): void {
    this.edgeExtensionsStore.selectEdgeExtensionClicked(id);
  }
}
